import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stepper, StepHeader } from "./PlanScreen";
import { SIGNUP_PATH, CREATE_WORKSPACE_PATH } from "../../utils/constants/routes";
import { signupEndpoints } from "../../utils/constants/httpConstants";
import { POST } from "../../Apis/apiWrapper";
import { saveSignupState, loadSignupState } from "../../utils/helpers/signupState";
import CustomEventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../utils/constants";
import "./onboarding.css";

const VerifyEmailPendingScreen = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const userData  = { ...loadSignupState(), ...(location.state || {}) };
  const email     = userData.email || "your email";
  const subdomain = userData.subdomain || "";

  // The first verification email is auto-sent at set-subdomain (the step that lands
  // us here), and the backend enforces a 60s/draft cooldown — so start the resend
  // button already in cooldown. The 429 retry_after is treated as the source of truth.
  const [cooldown, setCooldown] = useState(60);
  const [sending, setSending]   = useState(false);

  // Durable state: rehydrate / bounce to signup if no draft_id; keep persisted state current.
  useEffect(() => {
    if (!userData.draft_id) { navigate(SIGNUP_PATH, { replace: true }); return; }
    saveSignupState(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      const res = await POST(signupEndpoints.resendVerification, {
        draft_id: userData.draft_id,
      });
      CustomEventEmitter.emit(EMITTER_SUCCESS, res?.data?.message || "Verification email sent. Please check your inbox.");
      setCooldown(60);
    } catch (err) {
      // err is the axios response object (rethrown by the POST wrapper), or
      // undefined on a network/transport error.
      const status = err?.status;
      const msg    = err?.data?.message;
      if (status === 429) {
        // Rate-limited — trust retry_after and keep the button disabled that long.
        setCooldown(err?.data?.data?.retry_after || 60);
      } else if (status === 404) {
        // Signup draft not found — send them back to start.
        navigate(SIGNUP_PATH, { state: { error: msg } });
      } else if (status === 422) {
        // Workspace setup incomplete — route to the subdomain step to finish it.
        navigate(CREATE_WORKSPACE_PATH, { state: { error: msg } });
      } else {
        // 500 / unknown — generic toast, allow retry.
        CustomEventEmitter.emit(EMITTER_ERROR, msg || "Could not resend. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="onb-page">
      <StepHeader stepNumber={4} />
      <Stepper current={4} />

      <div className="onb-verify-outer">
        <div className="onb-verify-card">

          {/* Mail icon */}
          <div className="onb-verify-icon-ring">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>

          <h2>Check your inbox</h2>
          <p className="onb-sub">We've sent a verification link to:</p>

          {/* Email pill */}
          <div className="onb-email-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            {email}
          </div>

          {/* Step instructions */}
          <div className="onb-verify-steps">
            <div className="onb-vs">
              <div className="onb-vs-num">1</div>
              <span>Open the email from <strong>notifications@reachiq.ai</strong></span>
            </div>
            <div className="onb-vs">
              <div className="onb-vs-num">2</div>
              <span>Click the <strong>Verify Now</strong> button in the email</span>
            </div>
            <div className="onb-vs">
              <div className="onb-vs-num">3</div>
              <span>
                You'll be taken directly to Quick Setup.
              </span>
            </div>
          </div>

          <p className="onb-verify-note">
            Once verified,{" "}
            {subdomain ? (
              <><strong>{subdomain}.reachiq.ai</strong> will be live and ready to use.</>
            ) : (
              "your workspace will be live and ready to use."
            )}
          </p>

          {/* Resend link (inline text link kept as a tokenized .onb-lnk button —
              the DS Button is a full-width block CTA, not an inline link). While in
              cooldown the link shows the remaining seconds and is disabled; success
              and errors surface as global toasts. */}
          <div className="onb-resend-row" role="status" aria-live="polite">
            Didn't receive it?{" "}
            <button
              type="button"
              className="onb-lnk"
              onClick={handleResend}
              disabled={cooldown > 0 || sending}
            >
              {sending
                ? "Sending…"
                : cooldown > 0
                ? `Resend in ${cooldown}s`
                : "Resend verification email"}
            </button>
          </div>

          {/* Note about what happens when user clicks the link */}
          {/* <p className="onb-verify-footnote">
            Once verified, you'll be logged in automatically and taken to your workspace
            setup. No need to sign in again.
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPendingScreen;
